import React, { useState, useEffect } from 'react';

const BOOKING_URL = 'https://cryobybreum.planway.com/';
const PHONE_NUMBER = '+4526434402';
const PHONE_DISPLAY = '26 43 44 02';
const INSTAGRAM_URL = 'https://www.instagram.com/cryobybreum/';
const FACEBOOK_URL = 'https://www.facebook.com/cryobybreum/';

export default function FloatingActions() {
  const [isOpen, setIsOpen] = useState(false);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const actions = [
    {
      id: 'booking',
      label: 'Book online tid',
      tooltip: 'Book online',
      mobileLabel: 'Online booking',
      href: BOOKING_URL,
      isExternal: true,
      icon: 'fa-solid fa-calendar-check',
      bgColor: 'bg-[#9E7746] hover:bg-[#886435] active:bg-[#77552B]',
    },
    {
      id: 'phone',
      label: `Ring til os på ${PHONE_DISPLAY}`,
      tooltip: `Ring ${PHONE_DISPLAY}`,
      mobileLabel: `Ring ${PHONE_DISPLAY}`,
      href: `tel:${PHONE_NUMBER}`,
      isExternal: false,
      icon: 'fa-solid fa-phone',
      bgColor: 'bg-[#435C48] hover:bg-[#344939] active:bg-[#2A3B2E]',
    },
    {
      id: 'instagram',
      label: 'Følg Cryo by Breum på Instagram',
      tooltip: 'Instagram',
      mobileLabel: 'Instagram',
      href: INSTAGRAM_URL,
      isExternal: true,
      icon: 'fa-brands fa-instagram text-xl',
      bgColor: 'bg-[#995250] hover:bg-[#834240] active:bg-[#6E3634]',
    },
    {
      id: 'facebook',
      label: 'Find Cryo by Breum på Facebook',
      tooltip: 'Facebook',
      mobileLabel: 'Facebook',
      href: FACEBOOK_URL,
      isExternal: true,
      icon: 'fa-brands fa-facebook-f text-lg',
      bgColor: 'bg-[#3E5574] hover:bg-[#30435D] active:bg-[#26374C]',
    },
  ];

  return (
    <>
      {/* Mobile backdrop when expanded */}
      {isOpen && (
        <button
          type="button"
          aria-label="Luk menu"
          onClick={() => setIsOpen(false)}
          className="fixed inset-0 z-40 bg-black/25 backdrop-blur-[1px] sm:hidden transition-opacity cursor-default animate-fade-in"
        />
      )}

      <aside
        aria-label="Hurtig kontakt og booking"
        className="fixed right-4 sm:right-6 bottom-5 sm:bottom-6 z-50 flex flex-col items-end gap-3 sm:gap-3.5 print:hidden"
      >
        {/* Actions list: always visible on desktop, expandable on mobile */}
        <div
          className={`flex flex-col items-end gap-3 sm:gap-3.5 transition-all duration-300 ease-out origin-bottom-right ${
            isOpen
              ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto'
              : 'opacity-0 translate-y-4 scale-90 pointer-events-none sm:opacity-100 sm:translate-y-0 sm:scale-100 sm:pointer-events-auto'
          }`}
        >
          {actions.map((action) => (
            <a
              key={action.id}
              href={action.href}
              onClick={() => setIsOpen(false)}
              {...(action.isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              aria-label={action.label}
              className="group flex items-center gap-2.5 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-espresso"
            >
              {/* Mobile text tag pill */}
              <span
                data-floating-action=""
                className="sm:hidden px-3 py-1.5 bg-[#2B231F] text-[#F9F6F0] text-xs font-medium tracking-wide shadow-md whitespace-nowrap border border-[#E0D7CD]/20 select-none"
              >
                {action.mobileLabel}
              </span>

              {/* Action circle button */}
              <div
                data-floating-action=""
                className={`relative flex items-center justify-center w-12 h-12 sm:w-[50px] sm:h-[50px] text-white ${action.bgColor} shadow-[0_8px_18px_-2px_rgba(0,0,0,0.18),0_4px_8px_-2px_rgba(0,0,0,0.10)] hover:shadow-[0_12px_22px_-2px_rgba(0,0,0,0.26),0_6px_10px_-2px_rgba(0,0,0,0.14)] transition-all duration-200 ease-out hover:scale-110 active:scale-95`}
              >
                <i
                  className={`${action.icon} transition-transform duration-200 group-hover:scale-105`}
                  aria-hidden="true"
                />

                {/* Subtle tooltip label on hover (desktop only) */}
                <span
                  className="pointer-events-none absolute right-full mr-3 top-1/2 -translate-y-1/2 px-2.5 py-1 bg-[#2B231F] text-[#F9F6F0] text-[11px] font-medium tracking-wide shadow-md opacity-0 group-hover:opacity-100 transition-all duration-150 ease-out whitespace-nowrap hidden sm:block translate-x-1 group-hover:translate-x-0"
                >
                  {action.tooltip}
                </span>
              </div>
            </a>
          ))}
        </div>

        {/* Mobile toggle button (collapsed state) */}
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          aria-expanded={isOpen}
          aria-label={isOpen ? 'Luk hurtigmenu' : 'Åbn hurtig kontakt og booking'}
          data-floating-action=""
          className={`sm:hidden relative flex items-center justify-center w-12 h-12 text-white transition-all duration-300 ease-out shadow-[0_8px_20px_rgba(158,119,70,0.38)] active:scale-95 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-espresso ${
            isOpen ? 'bg-[#2B231F]' : 'bg-[#9E7746]'
          }`}
        >
          <i
            className={`fa-solid fa-plus text-lg transition-transform duration-300 ease-out ${
              isOpen ? 'rotate-45' : 'rotate-0'
            }`}
            aria-hidden="true"
          />
        </button>
      </aside>
    </>
  );
}
